import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '';

// Iter 637 (Kamel) — 📸 Souvenirs du voyage : photos partagées entre les voyageurs d'un même voyage.
export const Souvenirs = ({ f, token }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(0);
  const [grande, setGrande] = useState(null);
  const [legende, setLegende] = useState('');
  const [accordPub, setAccordPub] = useState(false); // Iter 850 — accord pour une publication sur les réseaux de l'association
  const [avis, setAvis] = useState('');
  const input = useRef(null);
  const load = useCallback(() => axios.get(`${API}/espace-adherent/${token}/souvenirs/${encodeURIComponent(f.destination)}`)
    .then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || e.message)), [token, f.destination]);
  useEffect(() => { load(); }, [load]);

  const envoyer = async (files) => {
    const liste = Array.from(files || []).slice(0, data?.quota_restant ?? 40);
    if (!liste.length) return;
    setErr(''); setBusy(liste.length);
    for (const file of liste) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('legende', legende);
      fd.append('accord_pub', accordPub ? '1' : '');
      fd.append('avis', accordPub ? avis : '');
      try { await axios.post(`${API}/espace-adherent/${token}/souvenirs/${f.reg_id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
      catch (e) { setErr(e.response?.data?.detail || e.message); }
      setBusy(b => b - 1);
    }
    setLegende(''); setAvis('');
    if (input.current) input.current.value = '';
    load();
  };
  const retirer = async (p) => {
    if (!window.confirm('Retirer cette photo des souvenirs du groupe ?')) return;
    try { await axios.delete(`${API}/espace-adherent/${token}/souvenirs/${p.id}`); setGrande(null); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  if (err && !data) return <p className="text-sm text-red-700" data-testid="souvenirs-erreur">{err}</p>;
  if (!data) return <p className="text-sm text-gray-400">Chargement des souvenirs…</p>;
  return (
    <div className="space-y-3" data-testid={`souvenirs-${f.reg_id}`}>
      <p className="text-sm text-gray-700">Partagez vos plus belles photos avec le groupe : tous les voyageurs de <strong>{f.voyage}</strong> les voient ici, pour toujours. Pas de photo gênante pour les autres, merci 😊</p>
      <div className="rounded-xl border-2 border-dashed border-[#1E3A5F]/40 bg-sky-50 p-3 space-y-2">
        <input value={legende} onChange={e => setLegende(e.target.value)} maxLength={200} placeholder="Légende (facultatif) : « Soirée au bord de la mer »" className="w-full text-sm px-3 py-2 rounded-lg border" data-testid="souvenirs-legende" />
        <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer" data-testid="souvenirs-accord-pub">
          <input type="checkbox" checked={accordPub} onChange={e => setAccordPub(e.target.checked)} className="mt-0.5" />
          <span>📣 <strong>J'autorise l'association</strong> à publier ces photos sur ses réseaux (Facebook, Instagram, TikTok, Snapchat, WhatsApp) pour donner envie aux prochains voyageurs. Facultatif — sans cette case, vos photos restent entre voyageurs. Vous pouvez demander leur retrait à tout moment.</span>
        </label>
        {accordPub && <textarea value={avis} onChange={e => setAvis(e.target.value)} maxLength={500} rows={2} placeholder="Un mot sur le voyage (facultatif) : « Super ambiance, l'hôtel était top, on revient l'an prochain ! »" className="w-full text-sm px-3 py-2 rounded-lg border" data-testid="souvenirs-avis" />}
        <input ref={input} type="file" accept="image/*" multiple onChange={e => envoyer(e.target.files)} className="hidden" data-testid="souvenirs-input" />
        <button type="button" onClick={() => input.current?.click()} disabled={busy > 0 || data.quota_restant === 0} data-testid="souvenirs-ajouter-btn"
          className="w-full py-3 rounded-xl bg-[#1E3A5F] text-white font-bold text-sm disabled:opacity-50">
          {busy > 0 ? `⏳ Envoi… (${busy} restante${busy > 1 ? 's' : ''})` : data.quota_restant === 0 ? 'Vous avez atteint le maximum de photos' : '📸 Ajouter des photos'}
        </button>
        {err && <p className="text-xs text-red-700" data-testid="souvenirs-erreur">{err}</p>}
      </div>
      {data.photos.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4" data-testid="souvenirs-vide">Aucune photo pour l'instant — soyez le premier à partager un souvenir !</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5" data-testid="souvenirs-grille">
          {data.photos.map(p => (
            <button type="button" key={p.id} onClick={() => setGrande(p)} className="relative aspect-square overflow-hidden rounded-lg bg-gray-100" data-testid={`souvenir-${p.id}`}>
              <img src={p.url} alt={p.legende || `Photo de ${p.auteur}`} loading="lazy" className="w-full h-full object-cover" />
              {p.mienne && <span className="absolute top-1 left-1 text-[9px] font-bold bg-white/90 text-[#1E3A5F] rounded px-1">moi</span>}
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-gray-500 text-center">{data.nb} photo{data.nb > 1 ? 's' : ''} partagée{data.nb > 1 ? 's' : ''} par le groupe</p>
      {grande && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-3" onClick={() => setGrande(null)} data-testid="souvenir-grande">
          <img src={grande.url} alt={grande.legende || ''} className="max-h-[75vh] max-w-full rounded-lg object-contain" />
          <div className="text-white text-center mt-3 space-y-1" onClick={e => e.stopPropagation()}>
            {grande.legende && <p className="text-sm font-bold">{grande.legende}</p>}
            <p className="text-xs text-gray-300">par {grande.auteur || 'un voyageur'} · {fmt(grande.at)}</p>
            <div className="flex gap-2 justify-center pt-1">
              <a href={grande.url} download target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-white text-[#1E3A5F] font-bold" data-testid="souvenir-telecharger">⬇️ Enregistrer</a>
              {grande.mienne && <button type="button" onClick={() => retirer(grande)} className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white font-bold" data-testid="souvenir-retirer">🗑 Retirer</button>}
              <button type="button" onClick={() => setGrande(null)} className="text-xs px-3 py-1.5 rounded-lg bg-gray-700 text-white font-bold" data-testid="souvenir-fermer">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Souvenirs;
